import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { WorkspaceFormValues } from '@/lib/validations/workspace'
import { workspaceService, userService } from '@/services'
import { Workspace, User } from '@/types'
import { useAuth } from '@/hooks/useAuth'
import { PageContainer, PageHeader, LoadingState } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { WorkspaceForm } from '@/components/workspaces/WorkspaceForm'

export default function WorkspaceEditPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [logo, setLogo] = useState<string | undefined>()
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [currentUsers, setCurrentUsers] = useState<User[]>([])
  const [usersToAdd, setUsersToAdd] = useState<User[]>([])
  const [usersToRemove, setUsersToRemove] = useState<string[]>([])
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  const loadWorkspace = useCallback(async () => {
    if (!workspaceId) return

    try {
      const data = await workspaceService.getById(workspaceId)
      if (data) {
        setWorkspace(data)
        setLogo(data.logo)
        setCurrentUsers(data.users)

        // Load all users for the association selector
        const users = await userService.getAll()
        setAllUsers(users)
      } else {
        toast.error('Workspace not found')
        navigate('/workspaces')
      }
    } catch {
      toast.error('Failed to load workspace')
    } finally {
      setIsLoading(false)
    }
  }, [workspaceId, navigate])

  useEffect(() => {
    loadWorkspace()
  }, [loadWorkspace])

  const handleRemoveCurrentUser = (userId: string) => {
    if (!workspace) return

    // Can't remove creator
    if (workspace.creator.id === userId) {
      toast.error('Cannot remove the workspace creator')
      return
    }

    // Can't remove yourself
    if (user?.id === userId) {
      toast.error('Cannot remove yourself from the workspace')
      return
    }

    setUsersToRemove([...usersToRemove, userId])
    setCurrentUsers(currentUsers.filter((u) => u.id !== userId))
  }

  const handleAddUser = (userId: string) => {
    const userToAdd = allUsers.find((u) => u.id === userId)
    if (userToAdd && !usersToAdd.some((u) => u.id === userId)) {
      setUsersToAdd([...usersToAdd, userToAdd])
    }
  }

  const handleRemoveUserToAdd = (userId: string) => {
    setUsersToAdd(usersToAdd.filter((u) => u.id !== userId))
  }

  // Get available users (not in current users, not in usersToAdd, not in usersToRemove)
  const availableUsers = allUsers.filter(
    (u) =>
      !currentUsers.some((cu) => cu.id === u.id) &&
      !usersToAdd.some((ua) => ua.id === u.id) &&
      !usersToRemove.includes(u.id)
  )

  const onSubmit = async (data: WorkspaceFormValues) => {
    if (!workspace || !user) return

    // Check permissions
    const isMember = workspace.users.some((u) => u.id === user.id)
    if (!isMember) {
      toast.error('You must be a member of this workspace to edit it')
      return
    }

    setIsSaving(true)
    try {
      // Update workspace details
      await workspaceService.update(workspace.id, {
        ...data,
        logo,
      } as Partial<Workspace>)

      // Remove users
      if (usersToRemove.length > 0) {
        try {
          for (const userId of usersToRemove) {
            await workspaceService.removeUser(workspace.id, userId)
          }
        } catch (error) {
          console.error('Failed to remove some users:', error)
          toast.warning('Workspace updated but some users could not be removed')
        }
      }

      // Add users
      if (usersToAdd.length > 0) {
        try {
          for (const userToAdd of usersToAdd) {
            await workspaceService.addUser(workspace.id, userToAdd.id)
          }
        } catch (error) {
          console.error('Failed to add some users:', error)
          toast.warning('Workspace updated but some users could not be added')
        }
      }

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Workspace updated successfully')
      navigate(`/workspaces/${workspace.id}`)
    } catch {
      toast.error('Failed to update workspace')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState text="Loading workspace..." />
      </PageContainer>
    )
  }

  if (!workspace) {
    return null
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title={workspace.name} titlePrefix="Editing Workspace:" />

      <WorkspaceForm
        mode="edit"
        defaultValues={{
          name: workspace.name,
          email: workspace.email,
          logo: workspace.logo,
        }}
        workspace={workspace}
        currentUser={user ?? undefined}
        logo={logo}
        onLogoChange={setLogo}
        currentUsers={currentUsers}
        usersToAdd={usersToAdd}
        usersToRemove={usersToRemove}
        availableUsers={availableUsers}
        onAddUser={handleAddUser}
        onRemoveUserToAdd={handleRemoveUserToAdd}
        onRemoveCurrentUser={handleRemoveCurrentUser}
        onSubmit={onSubmit}
        onCancel={() => navigate(`/workspaces/${workspace.id}`)}
        submitText={isSaving ? 'Saving...' : 'Save Changes'}
        loading={isSaving}
        title="Workspace Details"
        description="Update your workspace information"
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
